import { z } from 'zod';
import { Email, Id, IsoDate, Password, Slug, Url } from './common';
import { OfferLinkStatus } from './enums';
/* ============================================================================
   Offer links (PLAN §4.3): created in Mission HQ, shared manually (WhatsApp/QR),
   claimed on the public landing page → onboarding.
   ========================================================================== */
export const OfferPrefillSchema = z.object({
    companyName: z.string().max(80).optional(),
    ownerEmail: z.string().email().optional(),
    companyLogoUrl: Url.optional(),
    welcomeLine: z.string().max(200).optional(),
});
const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a 6-digit hex colour like #0457fd');
/**
 * Per-link landing page design (edited in Mission HQ → Offers, with a live preview).
 * Every field is optional: the landing page falls back to OFFER_DESIGN_DEFAULTS.
 * Text may contain {company} which is replaced with the prefilled company name.
 */
export const OfferDesignSchema = z.object({
    /** small pill above the headline, e.g. "Diwali collaboration · 2026" */
    chipText: z.string().trim().max(60).optional(),
    chipEmoji: z.string().trim().max(8).optional(),
    /** headline (first line) */
    title: z.string().trim().max(90).optional(),
    /** headline second line, shown in the accent colour (default "{company}") */
    highlight: z.string().trim().max(90).optional(),
    intro: z.string().trim().max(400).optional(),
    quote: z.string().trim().max(160).optional(),
    quoteBy: z.string().trim().max(60).optional(),
    ctaText: z.string().trim().max(40).optional(),
    /** "What your team gets" list (max 6) */
    perks: z.array(z.string().trim().min(1).max(80)).max(6).optional(),
    /** hero background gradient */
    bgFrom: Hex.optional(),
    bgTo: Hex.optional(),
    /** headline highlight, chip border and badge colour */
    accent: Hex.optional(),
    /** text colour on the hero background */
    heroText: z.enum(['light', 'dark']).optional(),
    pattern: z.enum(['diyas', 'grid', 'sparkles', 'none']).optional(),
});
export const OFFER_DESIGN_DEFAULTS = {
    chipText: 'Diwali collaboration · 2026',
    chipEmoji: '🪔',
    title: 'A Diwali collaboration for',
    highlight: '{company}',
    intro: 'As our Diwali gift, your team gets {plan} free for {months} months: every project, mission and deadline in one place, with your logo and your own domain.',
    quote: 'A small Diwali gift from us.',
    quoteBy: 'The DevNinjaWeb team',
    ctaText: 'Accept the mission',
    perks: ['Plan every project as missions', 'Kanban, list and calendar views', 'Sticky boards for ideas', 'Your own domain and branding'],
    bgFrom: '#0b1a4a',
    bgTo: '#0437b8',
    accent: '#f5b942',
    heroText: 'light',
    pattern: 'diyas',
};
/** Merge a stored design with defaults and fill {company} / {plan} / {months}. */
export function resolveOfferDesign(d, vars) {
    const m = { ...OFFER_DESIGN_DEFAULTS, ...Object.fromEntries(Object.entries(d ?? {}).filter(([, v]) => v !== undefined && v !== '')) };
    const fill = (t) => t.replace(/\{company\}/g, vars.company || 'your team').replace(/\{plan\}/g, vars.plan).replace(/\{months\}/g, String(vars.months));
    return { ...m, chipText: fill(m.chipText), title: fill(m.title), highlight: fill(m.highlight), intro: fill(m.intro), quote: fill(m.quote), quoteBy: fill(m.quoteBy), ctaText: fill(m.ctaText), perks: m.perks.map(fill) };
}
export const OfferFunnelSchema = z.object({
    opened: z.number().int().nonnegative(),
    started: z.number().int().nonnegative(),
    claimed: z.number().int().nonnegative(),
});
export const OfferLinkSchema = z.object({
    id: Id,
    offerId: Id,
    name: z.string(),
    slug: z.string(),
    url: Url,
    plan: z.object({ id: Id, name: z.string() }),
    durationMonths: z.number().int().positive(),
    claimBy: IsoDate,
    singleUse: z.boolean(),
    maxClaims: z.number().int().positive().nullable(),
    claimCount: z.number().int().nonnegative(),
    status: z.enum(OfferLinkStatus),
    prefill: OfferPrefillSchema,
    design: OfferDesignSchema,
    note: z.string().nullable(),
    funnel: OfferFunnelSchema,
    createdBy: z.string(),
    createdAt: IsoDate,
});
export const OfferLinkCreateSchema = z.object({
    name: z.string().trim().min(3).max(80),
    planId: Id,
    durationMonths: z.number().int().min(1).max(120),
    claimBy: IsoDate,
    singleUse: z.boolean(),
    maxClaims: z.number().int().min(1).max(10000).optional(),
    prefill: OfferPrefillSchema.optional(),
    design: OfferDesignSchema.optional(),
    note: z.string().max(500).optional(),
});
export const OfferLinkUpdateSchema = z.object({
    name: z.string().trim().min(3).max(80).optional(),
    claimBy: IsoDate.optional(),
    note: z.string().max(500).optional(),
    prefill: OfferPrefillSchema.optional(),
    design: OfferDesignSchema.optional(),
});
/** QR + share data for a link (mobile share sheet, WhatsApp deep link). */
export const OfferShareSchema = z.object({
    url: Url,
    whatsappUrl: Url,
    shareText: z.string(),
    /** QR matrix (true = dark module), rendered by the client to canvas/PNG */
    qr: z.array(z.array(z.boolean())),
});
/** Public offer landing page (claim.html). */
export const PublicOfferSchema = z.object({
    slug: z.string(),
    state: z.enum(['valid', 'claimed', 'expired', 'revoked']),
    companyName: z.string().nullable(),
    companyLogoUrl: z.string().url().nullable(),
    welcomeLine: z.string().nullable(),
    planName: z.string(),
    durationMonths: z.number().int().positive(),
    claimBy: IsoDate,
    highlights: z.array(z.string()),
    /** raw stored design; the page resolves it with resolveOfferDesign() */
    design: OfferDesignSchema,
    contact: z.object({ whatsapp: z.string().nullable(), email: z.string() }),
});
/** Claim → creates the tenant + owner and returns a session (onboarding step 1). */
export const OfferClaimSchema = z.object({
    companyName: z.string().trim().min(2).max(80),
    slug: Slug,
    ownerName: z.string().trim().min(2).max(80),
    ownerEmail: Email,
    password: Password,
    acceptTerms: z.literal(true),
});
export const SlugAvailabilitySchema = z.object({
    slug: z.string(),
    available: z.boolean(),
    reason: z.enum(['taken', 'reserved', 'invalid']).nullable(),
    suggestion: z.string().nullable(),
});
//# sourceMappingURL=offers.js.map